import React, { useState, useEffect, useCallback } from 'react'
import { subscriptionTiersAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import {
  Crown,
  Plus,
  Pencil,
  Trash2,
  X,
  Save,
  Check,
  AlertCircle,
  Users,
  Package,
  HardDrive,
  DollarSign,
  ToggleLeft,
  ToggleRight,
  GripVertical,
  ImageIcon,
  BarChart3,
  CreditCard,
  Layers,
  MessageCircle,
  Globe,
  Code,
  Clock,
} from 'lucide-react'

const EMPTY_TIER = {
  name: '',
  display_name: '',
  sort_order: 0,
  is_active: true,
  max_clients: 0,
  max_products: 0,
  max_users: 0,
  max_photo_categories: 0,
  storage_limit_bytes: 0,
  whatsapp_enabled: false,
  custom_domain: false,
  api_access: false,
  receive_payments_enabled: false,
  product_preferences_per_customer: false,
  analytics_enabled: false,
  price_monthly: 0,
  price_yearly: 0,
  promotional_price_enabled: false,
  promotional_price_monthly: 0,
  promotional_price_yearly: 0,
  trial_days: 0,
  stripe_price_id_monthly: '',
  stripe_price_id_yearly: '',
}

const BOOLEAN_FEATURES = [
  { key: 'whatsapp_enabled', label: 'WhatsApp', icon: MessageCircle },
  { key: 'custom_domain', label: 'Domínio personalizado', icon: Globe },
  { key: 'api_access', label: 'Acesso à API', icon: Code },
  { key: 'receive_payments_enabled', label: 'Receber pagamentos', icon: CreditCard },
  { key: 'product_preferences_per_customer', label: 'Preferências de produto por cliente', icon: Layers },
  { key: 'analytics_enabled', label: 'Análises', icon: BarChart3 },
]

const formatBytes = (bytes) => {
  if (bytes === -1) return 'Ilimitado'
  if (bytes === 0) return '0 B'
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${(bytes / Math.pow(1024, i)).toFixed(i > 1 ? 1 : 0)} ${sizes[i]}`
}

const formatLimit = (val) => (val === -1 ? 'Ilimitado' : val)

const ToggleSwitch = ({ checked, onChange, disabled = false, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    tabIndex={0}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange(!checked) } }}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
      checked ? 'bg-primary-600' : 'bg-admin-200'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    <span
      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
        checked ? 'translate-x-5' : 'translate-x-0'
      }`}
    />
  </button>
)

const TierFormModal = ({ tier, isNew, onSave, onClose }) => {
  const [form, setForm] = useState(tier ? { ...tier } : { ...EMPTY_TIER })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }))
    setError(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.name?.trim()) return setError('Informe o nome (slug)')
    if (!form.display_name?.trim()) return setError('Informe o nome de exibição')

    setSaving(true)
    setError(null)
    try {
      await onSave(form)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Falha ao salvar')
    } finally {
      setSaving(false)
    }
  }

  const storageGB = form.storage_limit_bytes === -1 ? -1 : form.storage_limit_bytes / (1024 * 1024 * 1024)

  const handleStorageChange = (val) => {
    const num = parseFloat(val)
    if (num === -1) return handleChange('storage_limit_bytes', -1)
    if (isNaN(num) || num < 0) return handleChange('storage_limit_bytes', 0)
    handleChange('storage_limit_bytes', Math.round(num * 1024 * 1024 * 1024))
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={onClose} />
        <div className="relative w-full max-w-3xl rounded-xl bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-admin-200 px-6 py-4">
            <h2 className="text-lg font-semibold text-admin-900">
              {isNew ? 'Criar plano de assinatura' : `Editar "${form.display_name}"`}
            </h2>
            <button
              onClick={onClose}
              className="rounded-md p-1 text-admin-400 hover:text-admin-600"
              aria-label="Fechar"
              tabIndex={0}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="max-h-[75vh] overflow-y-auto px-6 py-5 space-y-6">
            {error && (
              <div className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Basic Info */}
            <section>
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-admin-500">
                Informações básicas
              </h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label htmlFor="tier-name" className="block text-sm font-medium text-admin-700">
                    Nome (slug)
                  </label>
                  <input
                    id="tier-name"
                    type="text"
                    className="input mt-1"
                    placeholder="ex.: inicial"
                    value={form.name}
                    onChange={(e) => handleChange('name', e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                    disabled={!isNew}
                  />
                </div>
                <div>
                  <label htmlFor="tier-display" className="block text-sm font-medium text-admin-700">
                    Nome de exibição
                  </label>
                  <input
                    id="tier-display"
                    type="text"
                    className="input mt-1"
                    placeholder="ex.: Inicial"
                    value={form.display_name}
                    onChange={(e) => handleChange('display_name', e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="tier-order" className="block text-sm font-medium text-admin-700">
                    Ordem de exibição
                  </label>
                  <input
                    id="tier-order"
                    type="number"
                    className="input mt-1"
                    value={form.sort_order}
                    onChange={(e) => handleChange('sort_order', parseInt(e.target.value) || 0)}
                  />
                </div>
              </div>
              <div className="mt-3 flex items-center gap-3">
                <ToggleSwitch
                  checked={form.is_active}
                  onChange={(v) => handleChange('is_active', v)}
                  label="Ativo"
                />
                <span className="text-sm text-admin-600">Ativo</span>
              </div>
            </section>

            {/* Capacity Limits */}
            <section>
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-admin-500">
                Limites de capacidade
              </h3>
              <p className="mb-3 text-xs text-admin-400">Use -1 para ilimitado.</p>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                <div>
                  <label htmlFor="max-clients" className="flex items-center gap-1.5 text-sm font-medium text-admin-700">
                    <Users className="h-4 w-4" /> Máx. de clientes
                  </label>
                  <input
                    id="max-clients"
                    type="number"
                    className="input mt-1"
                    value={form.max_clients}
                    onChange={(e) => handleChange('max_clients', parseInt(e.target.value) || 0)}
                  />
                </div>
                <div>
                  <label htmlFor="max-products" className="flex items-center gap-1.5 text-sm font-medium text-admin-700">
                    <Package className="h-4 w-4" /> Máx. de produtos
                  </label>
                  <input
                    id="max-products"
                    type="number"
                    className="input mt-1"
                    value={form.max_products}
                    onChange={(e) => handleChange('max_products', parseInt(e.target.value) || 0)}
                  />
                </div>
                <div>
                  <label htmlFor="max-users" className="flex items-center gap-1.5 text-sm font-medium text-admin-700">
                    <Users className="h-4 w-4" /> Máx. de usuários
                  </label>
                  <input
                    id="max-users"
                    type="number"
                    className="input mt-1"
                    value={form.max_users}
                    onChange={(e) => handleChange('max_users', parseInt(e.target.value) || 0)}
                  />
                </div>
                <div>
                  <label htmlFor="max-photos" className="flex items-center gap-1.5 text-sm font-medium text-admin-700">
                    <ImageIcon className="h-4 w-4" /> Máx. de categorias de fotos
                  </label>
                  <input
                    id="max-photos"
                    type="number"
                    className="input mt-1"
                    value={form.max_photo_categories}
                    onChange={(e) => handleChange('max_photo_categories', parseInt(e.target.value) || 0)}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="storage" className="flex items-center gap-1.5 text-sm font-medium text-admin-700">
                    <HardDrive className="h-4 w-4" /> Limite de armazenamento (GB)
                  </label>
                  <input
                    id="storage"
                    type="number"
                    step="0.5"
                    className="input mt-1"
                    value={storageGB}
                    onChange={(e) => handleStorageChange(e.target.value)}
                  />
                  <p className="mt-1 text-xs text-admin-400">
                    {form.storage_limit_bytes === -1 ? 'Ilimitado' : formatBytes(form.storage_limit_bytes)}
                  </p>
                </div>
              </div>
            </section>

            {/* Feature Flags */}
            <section>
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-admin-500">
                Recursos
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {BOOLEAN_FEATURES.map(({ key, label, icon: Icon }) => (
                  <div key={key} className="flex items-center justify-between rounded-lg border border-admin-200 p-3">
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-admin-500" />
                      <span className="text-sm font-medium text-admin-700">{label}</span>
                    </div>
                    <ToggleSwitch
                      checked={form[key]}
                      onChange={(v) => handleChange(key, v)}
                      label={label}
                    />
                  </div>
                ))}
              </div>
            </section>

            {/* Pricing */}
            <section>
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-admin-500">
                Preços
              </h3>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="price-monthly" className="flex items-center gap-1.5 text-sm font-medium text-admin-700">
                    <DollarSign className="h-4 w-4" /> Preço mensal
                  </label>
                  <input
                    id="price-monthly"
                    type="number"
                    step="0.01"
                    className="input mt-1"
                    value={form.price_monthly}
                    onChange={(e) => handleChange('price_monthly', parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div>
                  <label htmlFor="price-yearly" className="flex items-center gap-1.5 text-sm font-medium text-admin-700">
                    <DollarSign className="h-4 w-4" /> Preço anual
                  </label>
                  <input
                    id="price-yearly"
                    type="number"
                    step="0.01"
                    className="input mt-1"
                    value={form.price_yearly}
                    onChange={(e) => handleChange('price_yearly', parseFloat(e.target.value) || 0)}
                  />
                </div>
              </div>

              <div className="mt-4 rounded-lg border border-admin-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-admin-700">Preço promocional</span>
                  <ToggleSwitch
                    checked={form.promotional_price_enabled}
                    onChange={(v) => handleChange('promotional_price_enabled', v)}
                    label="Ativar preço promocional"
                  />
                </div>
                {form.promotional_price_enabled && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="promo-monthly" className="block text-sm font-medium text-admin-700">
                        Promocional mensal
                      </label>
                      <input
                        id="promo-monthly"
                        type="number"
                        step="0.01"
                        className="input mt-1"
                        value={form.promotional_price_monthly}
                        onChange={(e) => handleChange('promotional_price_monthly', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <label htmlFor="promo-yearly" className="block text-sm font-medium text-admin-700">
                        Promocional anual
                      </label>
                      <input
                        id="promo-yearly"
                        type="number"
                        step="0.01"
                        className="input mt-1"
                        value={form.promotional_price_yearly}
                        onChange={(e) => handleChange('promotional_price_yearly', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-4 rounded-lg border border-admin-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-admin-700">Período de teste gratuito</span>
                </div>
                <div>
                  <label htmlFor="trial-days" className="flex items-center gap-1.5 text-sm font-medium text-admin-700">
                    <Clock className="h-4 w-4" /> Dias de teste
                  </label>
                  <input
                    id="trial-days"
                    type="number"
                    min="0"
                    className="input mt-1"
                    value={form.trial_days}
                    onChange={(e) => handleChange('trial_days', parseInt(e.target.value) || 0)}
                  />
                  <p className="mt-1 text-xs text-admin-400">
                    {form.trial_days > 0
                      ? `Novos assinantes ganham ${form.trial_days} dias de teste gratuito antes da cobrança.`
                      : 'Sem período de teste. Os assinantes são cobrados imediatamente.'}
                  </p>
                </div>
              </div>

              <div className="mt-4 rounded-lg border border-admin-200 p-4">
                <h4 className="mb-3 text-sm font-medium text-admin-700">IDs de preço do Stripe</h4>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="stripe-monthly" className="block text-sm font-medium text-admin-700">
                      ID do preço mensal
                    </label>
                    <input
                      id="stripe-monthly"
                      type="text"
                      className="input mt-1 font-mono text-xs"
                      placeholder="price_..."
                      value={form.stripe_price_id_monthly || ''}
                      onChange={(e) => handleChange('stripe_price_id_monthly', e.target.value)}
                    />
                  </div>
                  <div>
                    <label htmlFor="stripe-yearly" className="block text-sm font-medium text-admin-700">
                      ID do preço anual
                    </label>
                    <input
                      id="stripe-yearly"
                      type="text"
                      className="input mt-1 font-mono text-xs"
                      placeholder="price_..."
                      value={form.stripe_price_id_yearly || ''}
                      onChange={(e) => handleChange('stripe_price_id_yearly', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </section>
          </form>

          <div className="flex items-center justify-end gap-3 border-t border-admin-200 px-6 py-4">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancelar
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={saving}
              className="btn btn-primary flex items-center gap-2"
            >
              {saving ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {isNew ? 'Criar plano' : 'Salvar alterações'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export const SubscriptionTiersPage = () => {
  const { isOwner } = useAuth()
  const [tiers, setTiers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editingTier, setEditingTier] = useState(null)
  const [isCreating, setIsCreating] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const loadTiers = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await subscriptionTiersAPI.getAll({ include_inactive: 'true' })
      setTiers(res.data?.data || [])
    } catch (err) {
      setError(err?.response?.data?.message || 'Falha ao carregar os planos de assinatura')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadTiers()
  }, [loadTiers])

  const handleSave = async (formData) => {
    if (editingTier) {
      await subscriptionTiersAPI.update(editingTier.id, formData)
    } else {
      await subscriptionTiersAPI.create(formData)
    }
    setEditingTier(null)
    setIsCreating(false)
    loadTiers()
  }

  const handleDelete = async (tier) => {
    try {
      await subscriptionTiersAPI.remove(tier.id)
      setDeleteConfirm(null)
      loadTiers()
    } catch (err) {
      alert(err?.response?.data?.message || 'Falha ao desativar o plano')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
          <p className="text-admin-500">Carregando planos de assinatura...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-admin-900 flex items-center gap-2">
            <Crown className="h-7 w-7 text-primary-600" />
            Planos de assinatura
          </h1>
          <p className="mt-1 text-sm text-admin-500">
            Gerencie os planos de assinatura e seus recursos, limites e preços.
          </p>
        </div>
        {isOwner && (
        <button
          onClick={() => { setIsCreating(true); setEditingTier(null) }}
          className="btn btn-primary flex items-center gap-2"
          aria-label="Criar novo plano"
          tabIndex={0}
        >
          <Plus className="h-4 w-4" />
          Novo plano
        </button>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 p-4 text-sm text-red-700">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Tiers Grid */}
      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
        {tiers.map((tier) => (
          <div
            key={tier.id}
            className={`card relative overflow-hidden transition-shadow hover:shadow-lg ${
              !tier.is_active ? 'opacity-60' : ''
            }`}
          >
            {/* Header stripe */}
            <div className={`absolute inset-x-0 top-0 h-1 ${tier.is_active ? 'bg-primary-500' : 'bg-admin-300'}`} />

            <div className="p-5">
              {/* Title row */}
              <div className="mb-4 flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-bold text-admin-900">{tier.display_name}</h3>
                  <p className="text-xs text-admin-400 font-mono">{tier.name}</p>
                </div>
                <div className="flex items-center gap-1">
                  {!tier.is_active && (
                    <span className="mr-1 rounded-full bg-admin-100 px-2 py-0.5 text-xs text-admin-500">
                      Inativo
                    </span>
                  )}
                  {isOwner && (
                  <button
                    onClick={() => { setEditingTier(tier); setIsCreating(false) }}
                    className="rounded-md p-1.5 text-admin-400 hover:bg-admin-100 hover:text-admin-600"
                    aria-label={`Editar ${tier.display_name}`}
                    tabIndex={0}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  )}
                  {isOwner && (
                  <button
                    onClick={() => setDeleteConfirm(tier)}
                    className="rounded-md p-1.5 text-admin-400 hover:bg-red-50 hover:text-red-500"
                    aria-label={`Desativar ${tier.display_name}`}
                    tabIndex={0}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  )}
                </div>
              </div>

              {/* Pricing */}
              <div className="mb-4">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-admin-900">
                    R${tier.promotional_price_enabled ? tier.promotional_price_monthly : tier.price_monthly}
                  </span>
                  <span className="text-sm text-admin-500">/mês</span>
                </div>
                {tier.promotional_price_enabled && (
                  <p className="text-xs text-admin-400 line-through">R${tier.price_monthly}/mês</p>
                )}
                {tier.price_yearly > 0 && (
                  <p className="text-xs text-admin-500 mt-0.5">
                    R${tier.promotional_price_enabled ? tier.promotional_price_yearly : tier.price_yearly}/ano
                  </p>
                )}
                {tier.trial_days > 0 && (
                  <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                    <Clock className="h-3 w-3" />
                    {tier.trial_days} dias de teste grátis
                  </span>
                )}
              </div>

              {/* Limits */}
              <div className="mb-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1.5 text-admin-600">
                    <Users className="h-3.5 w-3.5" /> Clientes
                  </span>
                  <span className="font-medium text-admin-900">{formatLimit(tier.max_clients)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1.5 text-admin-600">
                    <Package className="h-3.5 w-3.5" /> Produtos
                  </span>
                  <span className="font-medium text-admin-900">{formatLimit(tier.max_products)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1.5 text-admin-600">
                    <Users className="h-3.5 w-3.5" /> Usuários
                  </span>
                  <span className="font-medium text-admin-900">{formatLimit(tier.max_users)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1.5 text-admin-600">
                    <ImageIcon className="h-3.5 w-3.5" /> Categorias de fotos
                  </span>
                  <span className="font-medium text-admin-900">{formatLimit(tier.max_photo_categories)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1.5 text-admin-600">
                    <HardDrive className="h-3.5 w-3.5" /> Armazenamento
                  </span>
                  <span className="font-medium text-admin-900">{formatBytes(tier.storage_limit_bytes)}</span>
                </div>
              </div>

              {/* Features */}
              <div className="space-y-1.5">
                {BOOLEAN_FEATURES.map(({ key, label, icon: Icon }) => (
                  <div key={key} className="flex items-center gap-2 text-sm">
                    {tier[key] ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <X className="h-4 w-4 text-admin-300" />
                    )}
                    <span className={tier[key] ? 'text-admin-700' : 'text-admin-400'}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {tiers.length === 0 && !loading && (
        <div className="rounded-xl border-2 border-dashed border-admin-200 p-12 text-center">
          <Crown className="mx-auto h-12 w-12 text-admin-300" />
          <h3 className="mt-4 text-lg font-medium text-admin-900">Nenhum plano de assinatura</h3>
          <p className="mt-1 text-sm text-admin-500">Comece criando seu primeiro plano.</p>
          {isOwner && (
          <button
            onClick={() => { setIsCreating(true); setEditingTier(null) }}
            className="btn btn-primary mt-4"
          >
            <Plus className="mr-2 h-4 w-4 inline" />
            Criar plano
          </button>
          )}
        </div>
      )}

      {/* Edit / Create Modal */}
      {(editingTier || isCreating) && (
        <TierFormModal
          tier={editingTier}
          isNew={isCreating}
          onSave={handleSave}
          onClose={() => { setEditingTier(null); setIsCreating(false) }}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setDeleteConfirm(null)} />
          <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-semibold text-admin-900">Desativar plano</h3>
            <p className="mt-2 text-sm text-admin-600">
              Tem certeza de que deseja desativar <strong>{deleteConfirm.display_name}</strong>?
              Os estúdios que estão neste plano manterão seus limites atuais.
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="btn btn-secondary">
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
              >
                Desativar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
